<template>
  <v-card>
    <v-card-title>
      <span class="text-h5 gray--text">Terapêutica</span>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <v-row dense align="center">
          <v-col cols="auto">
            <v-switch
              label="Dilatação"
              v-model="dilation"
              dense
              hide-details
              @change="onDilationChange"
              class="mt-0"
            />
          </v-col>

          <v-col>
            <v-checkbox
              v-model="refract"
              label="Refração"
              class="mt-0"
              hide-details
              dense
              :disabled="disableRefract"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <SearchDropdown
              v-model="medicine"
              :search-result-items="medicineList"
              :loading="isMedicineListLoading"
              label="Medicamento"
              item-text="name"
              input-prompt-text="Digite o nome de um medicamento para pesquisar..."
              not-found-text="Nenhum medicamento encontrado."
              :error-messages="errors.medicine"
              @search="triggerMedicineSearch"
              @change="onMedicineChange"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="applicationRoute"
              outlined
              dense
              label="Via de aplicação"
              :items="applicationRoutes"
              item-text="name"
              :hide-details="!errors.applicationRoute"
              :error-messages="errors.applicationRoute"
              @change="onApplicationRouteChange"
              background-color="white"
              :disabled="!applicationRoutes.length"
              placeholder="Selecione um medicamento."
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="applicationSite"
              outlined
              dense
              label="Local"
              :items="therapyApplicationSites"
              item-text="name"
              :hide-details="!errors.applicationSite"
              :error-messages="errors.applicationSite"
              background-color="white"
              :disabled="!therapyApplicationSites.length"
              placeholder="Selecione uma via de aplicação."
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="interval"
              outlined
              dense
              label="Intervalo"
              :items="intervalList"
              item-text="name"
              :hide-details="!errors.interval"
              :error-messages="errors.interval"
              background-color="white"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="numberOfApplications"
              outlined
              dense
              label="N° de aplicações"
              :items="numberOfApplicationsList"
              item-text="name"
              :hide-details="!errors.numberOfApplications"
              :error-messages="errors.numberOfApplications"
              background-color="white"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="auto">
            <v-btn
              color="light-blue"
              dark
              @click="saveAttendanceTherapy"
              :disabled="disabled"
            >
              Salvar
            </v-btn>
          </v-col>

          <v-col cols="auto">
            <v-btn color="gray lighten-1" dark @click="closeForm">
              Cancelar
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import SearchDropdown from "@/components/ui/form/search_dropdown.vue";
import {
  intervalList,
  findIntervalByValue,
} from "@/components/attendance_therapy/form/intervalList.js";
import {
  numberOfApplicationsList,
  findNumberOfApplicationByValue,
} from "@/components/attendance_therapy/form/numberOfApplicationsList.js";

const EMPTY_FIELD_MESSAGE = "Esse campo deve ser preenchido.";

export default {
  name: "AttendanceTherapyFormCard",
  components: { SearchDropdown },
  props: {
    attendance: {
      type: Object,
      required: true,
    },
    therapy: {
      type: Object,
      required: false,
    },
    editView: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      medicineSearch: "",
      errors: {},
      medicine: null,
      attendanceTherapy: "",
      applicationRoute: "",
      applicationSite: "",
      interval: "",
      numberOfApplications: "",
      dilation: true,
      refract: false,
    };
  },
  apollo: {
    medicines: {
      query: require("@/graphql/medicine/medicines.gql"),
      variables() {
        return {
          dilation: this.dilation,
          searchQuery: this.medicineQuerySearch,
          disabled: false,
        };
      },
      update({ medicines }) {
        return medicines.collection;
      },
      result() {
        this.updateApplicationRoute();
      },
      skip: true,
    },
    therapyMedicine: {
      query: require("@/graphql/medicine/medicine_with_application_routes.gql"),
      variables() {
        return {
          id: this.therapy.medicine.id,
        };
      },
      update({ medicine }) {
        return medicine;
      },
      skip() {
        return !this.therapy?.medicine.id;
      },
    },
  },
  mounted() {
    this.loadTherapy();
  },
  computed: {
    disableRefract() {
      return !this.dilation;
    },
    intervalList() {
      return intervalList;
    },
    numberOfApplicationsList() {
      return numberOfApplicationsList;
    },
    medicineList() {
      return this.medicines || [];
    },
    isMedicineListLoading() {
      return this.$apollo.queries.medicines.loading;
    },
    applicationRoutes() {
      return this.medicine?.applicationRoutes || [];
    },
    therapyApplicationSites() {
      return this.applicationRoute?.applicationSites || [];
    },
    isValid() {
      return Object.keys(this.errors).length === 0;
    },
  },
  watch: {
    therapyMedicine(medicine) {
      this.medicine = medicine;

      this.loadTherapyApplicationRoute();
      this.loadTherapyApplicationSite();
    },
  },
  methods: {
    loadTherapyApplicationRoute() {
      const applicationRoutes = this.medicine.applicationRoutes;
      const therapyApplicationRouteId = this.therapy.applicationRoute.id;

      this.applicationRoute = applicationRoutes.find((applicationRoute) => {
        return applicationRoute.id === therapyApplicationRouteId;
      });
    },
    loadTherapyApplicationSite() {
      const applicationSites = this.applicationRoute.applicationSites;
      const selectedApplicationSiteId = this.therapy.applicationSite.id;

      this.applicationSite = applicationSites.find((applicationSite) => {
        return applicationSite.id === selectedApplicationSiteId;
      });
    },
    triggerMedicineSearch(search) {
      this.medicineQuerySearch = search;
      this.$apollo.queries.medicines.skip = false;
    },
    updateApplicationRoute() {
      if (!this.applicationRoute) return;

      this.applicationRoute = this.applicationRoutes?.find(
        ({ id }) => this.applicationRoute.id === id
      );
    },
    filterDilationMedicines() {
      const eyeApplicationRoute = "uso ocular";

      return this.medicines.filter((medicine) =>
        medicine.applicationRoutes
          .map((e) => e.name)
          .includes(eyeApplicationRoute)
      );
    },
    onDilationChange() {
      this.refract = false;
      this.medicine = null;

      this.clearForm();

      if (this.dilation) {
        this.applyDefaultDilationValues();
      }
    },
    onMedicineChange() {
      this.applicationRoute = "";
      this.applicationSite = "";

      if (this.dilation) {
        this.applyDefaultApplicationValuesForDilation();
      }
    },
    applyDefaultApplicationValuesForDilation() {
      this.applicationRoute = this.findApplicationRouteByName("uso ocular");
      this.applicationSite = this.findApplicationSiteByName(
        "em ambos os olhos"
      );
    },
    findApplicationRouteByName(name) {
      return this.applicationRoutes?.find((entry) => entry.name === name);
    },
    findApplicationSiteByName(name) {
      return this.therapyApplicationSites?.find((entry) => entry.name === name);
    },
    onApplicationRouteChange() {
      this.applicationSite = "";
    },
    loadTherapy() {
      if (!this.therapy) return;

      this.attendanceTherapyId = this.therapy.id;
      this.dilation = this.therapy.dilation;
      this.refract = this.therapy.refract;
      this.interval = findIntervalByValue(this.therapy.interval);
      this.numberOfApplications = findNumberOfApplicationByValue(
        this.therapy.numberOfApplications
      );
    },
    async saveAttendanceTherapy() {
      if (this.disabled) return;

      this.validate();
      if (!this.isValid) return;

      await this.apolloMutate({
        mutation: require("@/graphql/attendance/save_attendance_therapy.gql"),
        variables: {
          attendanceId: this.attendance.id,
          attendanceTherapyId: this.attendanceTherapyId,
          medicineId: this.medicine.id,
          applicationRouteId: this.applicationRoute.id,
          applicationSiteId: this.applicationSite.id,
          interval: this.interval.value,
          numberOfApplications: this.numberOfApplications.value,
          dilation: this.dilation,
          refract: this.refract,
        },
      });

      if (!this.editView) {
        this.clearForm();
      }

      this.closeForm();
    },
    validate() {
      this.errors = {};

      if (!this.medicine?.id) {
        this.errors.medicine = EMPTY_FIELD_MESSAGE;
      }
      if (!this.applicationRoute?.id) {
        this.errors.applicationRoute = EMPTY_FIELD_MESSAGE;
      }
      if (!this.applicationSite?.id) {
        this.errors.applicationSite = EMPTY_FIELD_MESSAGE;
      }
      if (!this.interval?.value) {
        this.errors.interval = EMPTY_FIELD_MESSAGE;
      }
      if (!this.numberOfApplications?.value) {
        this.errors.numberOfApplications = EMPTY_FIELD_MESSAGE;
      }
    },
    clearForm() {
      this.medicine = {};
      this.applicationRoute = "";
      this.applicationSite = "";
      this.interval = "";
      this.numberOfApplications = "";
    },
    closeForm() {
      this.$emit("close");
    },
    applyDefaultDilationValues() {
      this.numberOfApplications = findNumberOfApplicationByValue(3);
      this.interval = findIntervalByValue(5);
    },
  },
};
</script>
